import { useState, useEffect, useRef } from "react";
import { Link, NavigateOptions, useLocation } from "react-router-dom";
import _ from "lodash";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/sharp-regular-svg-icons";
import { Progress } from "@mantine/core";
import PathConstants from "../../../../routes/pathConstants";
import { useProductContext } from "../../../../contexts/ProductContext";
import { appealsFlowRoutes } from "../../../../routes/appealsFlowRoutes";
import NavigateBackButton from "./NavigateBackButton";

import styles from "./header.module.scss";

export type HeaderProps = {
  back?:
    | string
    | {
        route: string;
        options: NavigateOptions;
      }
    | (() => void);
  hasMenuButton?: boolean;
  hasFullMenu?: boolean;
  isClaimableDark?: boolean;
  progressProps?: {
    hasProgressTracker: boolean;
    progressPercent?: number;
  };
  className?: string;
};

export default function Header({
  back,
  hasMenuButton,
  hasFullMenu,
  isClaimableDark,
  progressProps,
  className,
}: HeaderProps) {
  const { userState, setUserState } = useProductContext();

  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const { signOut } = useAuthenticator((context) => [context.user]);

  const goHome = () => {
    const { pathname } = location;

    if (!_.includes(appealsFlowRoutes, pathname)) {
      window.location.assign(PathConstants.Home);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogoutClick = () => {
    setUserState({ ...userState, authenticated: false });
    signOut();

    localStorage.clear();
    sessionStorage.clear();

    window.location.assign(PathConstants.Home);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  const menuContainer = isMenuOpen ? (
    <div className={styles.mainMenu}>
      <div className={styles.mainMenuItems}>
        {hasFullMenu && (
          <>
            <Link to={PathConstants.AccountOverview}>
              <div className={styles.item}>
                <p>My Account</p>
              </div>
            </Link>
            <Link to={PathConstants.Appeals}>
              <div className={styles.item}>
                <p>My Appeals</p>
              </div>
            </Link>
          </>
        )}
        <button
          className={styles.item}
          onClick={handleLogoutClick}
          type="button"
        >
          {"Logout"}
        </button>
      </div>
    </div>
  ) : null;

  const menuButton = hasMenuButton ? (
    <button
      className={styles.menuContainer}
      onClick={toggleMenu}
      type="button"
      aria-label="open menu"
    >
      <FontAwesomeIcon icon={faBars} />
    </button>
  ) : (
    <div className={styles.menuButtonFiller} />
  );

  const progressTrackerContainer =
    progressProps &&
    progressProps.hasProgressTracker &&
    progressProps.progressPercent ? (
      <Progress
        value={progressProps.progressPercent}
        classNames={{ root: styles.progressBar }}
        color="rgba(37, 36, 35, 1)"
        size="lg"
        transitionDuration={500}
        radius={0}
      />
    ) : null;

  const logo = isClaimableDark ? (
    <a onClick={goHome}>
      <img
        className={styles.logoDark}
        src="/images/logo/logo_volt.svg"
        alt="Claimable"
      />
    </a>
  ) : (
    <a onClick={goHome}>
      <img
        className={styles.logo}
        src="/images/logo/logo_black_large.svg"
        alt="Claimable"
      />
    </a>
  );

  const headerClassNames = [styles.pageContentHeaderOuter];

  if (className) {
    headerClassNames.push(className);
  }

  if (isClaimableDark) {
    headerClassNames.push(styles.pageContentHeaderOuter__darkMode);
  }

  return (
    <div className={headerClassNames.join(" ")}>
      <div className={styles.pageContentHeaderInner}>
        <div className={styles.logoBar}>
          <NavigateBackButton
            route={
              typeof back === "string" || typeof back === "function"
                ? back
                : back?.route
            }
            navigateOptions={
              typeof back === "object" ? back?.options : undefined
            }
          />
          <div className={styles.logoContainer}>{logo}</div>
          {menuButton}
        </div>
        {progressTrackerContainer !== null ? (
          <div className={styles.trackerExteriorContainer}>
            <div className={styles.trackerOuterContainer}>
              {progressTrackerContainer}
            </div>
          </div>
        ) : null}
      </div>
      {menuContainer}
    </div>
  );
}
